import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/api';
import { ConnectionRequest, Child, SearchResult } from '../types';
import PhoneInput from './PhoneInput';
import './ConnectionsScreen.css';

interface ConnectionsScreenProps {
  cameFromActivity?: boolean;
  onReturnToActivity?: () => void;
  returnToActivityUrl?: string;
  onForceTabUpdate?: (tab: string) => void;
  cameFromOnboarding?: boolean;
  onContinueOnboarding?: () => void;
}

const ConnectionsScreen: React.FC<ConnectionsScreenProps> = ({ cameFromActivity = false, onReturnToActivity, returnToActivityUrl, onForceTabUpdate, cameFromOnboarding = false, onContinueOnboarding }) => {
  const navigate = useNavigate();
  const [connectionRequests, setConnectionRequests] = useState<ConnectionRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<any[]>([]);
  const [activeConnections, setActiveConnections] = useState<any[]>([]);
  const [searchMethod, setSearchMethod] = useState<'email' | 'phone'>('email');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedParent, setSelectedParent] = useState<SearchResult | null>(null);
  const [myChildren, setMyChildren] = useState<Child[]>([]);
  const [selectedMyChild, setSelectedMyChild] = useState<string | null>(null);
  const [selectedTargetChildren, setSelectedTargetChildren] = useState<string[]>([]);
  const [connectionMessage, setConnectionMessage] = useState('');
  const [showReturnToActivityPopup, setShowReturnToActivityPopup] = useState(false);
  const [showSkeletonModal, setShowSkeletonModal] = useState(false);
  const [noResultsContact, setNoResultsContact] = useState('');
  const [skeletonParentName, setSkeletonParentName] = useState('');
  const [skeletonChildName, setSkeletonChildName] = useState('');
  const [skeletonChildFirstName, setSkeletonChildFirstName] = useState('');
  const [skeletonChildLastName, setSkeletonChildLastName] = useState('');
  const [skeletonChildBirthYear, setSkeletonChildBirthYear] = useState('');
  const apiService = ApiService.getInstance();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    loadConnectionRequests();
    loadSentRequests();
    loadActiveConnections();
    loadMyChildren();

    // Set up polling for connection requests every 30 seconds
    const interval = setInterval(() => {
      loadConnectionRequests();
      loadSentRequests();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loadConnectionRequests = async () => {
    try {
      setLoading(true);
      const response = await apiService.getConnectionRequests();
      
      if (response.success && response.data) {
        setConnectionRequests(response.data);
      } else {
        console.error('❌ Failed to load connection requests:', response.error);
        alert(`Error: ${response.error || 'Failed to load connection requests'}`);
      }
    } catch (error) {
      console.error('❌ Load connection requests error:', error);
      alert('Failed to load connection requests');
    } finally {
      setLoading(false);
    }
  };

  const loadSentRequests = async () => {
    try {
      const response = await apiService.getSentConnectionRequests();
      
      if (response.success && response.data) {

        // Log each request individually to check for skeleton connections
        response.data.forEach((request, index) => {
        });

        setSentRequests(response.data);
      } else {
        console.error('❌ Failed to load sent requests:', response.error);
      }
    } catch (error) {
      console.error('❌ Load sent requests error:', error);
    }
  };

  const loadMyChildren = async () => {
    try {
      const response = await apiService.getChildren();
      if (response.success && response.data) {
        setMyChildren(response.data);
      }
    } catch (error) {
      console.error('Load children error:', error);
    }
  };

  const loadActiveConnections = async () => {
    try {
      const response = await apiService.getConnections();
      if (response.success && response.data) {
        setActiveConnections(response.data);
      } else {
        console.error('Failed to load active connections:', response.error);
      }
    } catch (error) {
      console.error('Load active connections error:', error);
    }
  };

  const handleSearchParent = async () => {
    const searchValue = searchMethod === 'email' ? searchEmail.trim() : searchPhone.trim();

    if (!searchValue || searchValue.length < 3) {
      alert('Please enter at least 3 characters to search');
      return;
    }

    setSearching(true);
    try {
      const response = await apiService.searchParent(searchValue, searchMethod);
      if (response.success && response.data) {
        setSearchResults(response.data);
        if (response.data.length === 0) {
          // No results found - offer to create skeleton account
          setNoResultsContact(searchValue);
          setShowSkeletonModal(true);
        }
      } else {
        alert(`Error: ${response.error || 'Search failed'}`);
      }
    } catch (error) {
      alert('Search failed');
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleAcceptRequest = async (requestUuid: string) => {
    if (window.confirm('Are you sure you want to accept this connection request?')) {
      try {
        const response = await apiService.respondToConnectionRequest(requestUuid, 'accept');
        if (response.success) {
          // Find the connection request details before removing it from state
          const acceptedRequest = connectionRequests.find(req => req.request_uuid === requestUuid);
          
          setConnectionRequests(prev => prev.filter(req => req.request_uuid !== requestUuid));
          // Reload active connections to show the new connection immediately
          loadActiveConnections();
          
          // Auto-notify: Now handled on backend for bidirectional notifications
          
          alert('Connection request accepted!');
        } else {
          alert(`Error: ${response.error || 'Failed to accept request'}`);
        }
      } catch (error) {
        alert('Failed to accept request');
        console.error('Accept request error:', error);
      }
    }
  };

  const notifyNewConnectionAboutFutureActivities = async (
    newConnectionParentUuid: string, 
    requestorChildUuid: string,
    targetChildUuid?: string
  ) => {
    try {

      const today = new Date().toISOString().split('T')[0];
      const futureDate = new Date();
      futureDate.setFullYear(futureDate.getFullYear() + 1); // Get activities for the next year
      const endDate = futureDate.toISOString().split('T')[0];


      // Get all future activities for the accepting parent (current user)
      const activitiesResponse = await apiService.getCalendarActivities(today, endDate);
      
      
      if (activitiesResponse.success && activitiesResponse.data) {

        // Filter for activities that are in the future, hosted by current user, and have auto-notify enabled
        const futureActivities = activitiesResponse.data.filter(activity => {
          const activityDate = new Date(activity.start_date);
          const now = new Date();
          const isFuture = activityDate >= now;
          const isHost = activity.is_host;
          const hasAutoNotify = activity.auto_notify_new_connections;
          
          
          return isFuture && isHost && hasAutoNotify;
        });


        if (futureActivities.length === 0) {
        }

        // Send invitations for each future activity
        let invitationsSent = 0;
        for (const activity of futureActivities) {
          try {
            // Determine which child to invite based on the connection request
            const inviteChildUuid = targetChildUuid || requestorChildUuid;
            
            
            const inviteResponse = await apiService.sendActivityInvitation(
              activity.uuid || activity.activity_uuid || String(activity.id),
              newConnectionParentUuid,
              inviteChildUuid,
              `Welcome to our connection! ${activity.child_name} would like to invite your child to join: ${activity.name}`
            );
            
            
            if (inviteResponse.success) {
              invitationsSent++;
            }
          } catch (inviteError) {
            console.error('❌ Failed to send activity invitation:', inviteError);
          }
        }

      }
    } catch (error) {
      console.error('❌ Failed to notify new connection about future activities:', error);
    }
  };


  const handleRejectRequest = async (requestUuid: string) => {
    if (window.confirm('Are you sure you want to reject this connection request?')) {
      try {
        const response = await apiService.respondToConnectionRequest(requestUuid, 'reject');
        if (response.success) {
          setConnectionRequests(prev => prev.filter(req => req.request_uuid !== requestUuid));
          alert('Connection request rejected');
        } else {
          alert(`Error: ${response.error || 'Failed to reject request'}`);
        }
      } catch (error) {
        alert('Failed to reject request');
        console.error('Reject request error:', error);
      }
    }
  };

  const handleConnectRequest = (parent: SearchResult) => {
    setSelectedParent(parent);
    setSelectedMyChild(null); // User must explicitly select their child
    // Start with no children selected - user must explicitly choose
    setSelectedTargetChildren([]);
    setConnectionMessage('');
    setShowConnectModal(true);
  };

  const handleRemoveConnection = async (connectionId: string, connectionDescription: string) => {
    if (window.confirm(`Are you sure you want to remove this connection? ${connectionDescription}`)) {
      try {
        const response = await apiService.deleteConnection(connectionId);
        
        if (response.success) {
          setActiveConnections(prev => prev.filter(conn => conn.connection_uuid !== connectionId));
          alert('Connection removed successfully');
        } else {
          alert(`Error: ${response.error || 'Failed to remove connection'}\n\nAlternative: You can test auto-notifications by:\n1. Creating a new test account\n2. Or having the other user send you a new connection request`);
        }
      } catch (error) {
        console.error('❌ Remove connection error:', error);
        alert(`Failed to remove connection (API endpoint may not exist yet)\n\nTo test auto-notifications, you can:\n1. Create a new test user account\n2. Send a connection request from that new account\n3. Accept it to trigger auto-notifications\n\nOr ask me to help implement the delete endpoint on the backend.`);
      }
    }
  };

  const handleSendConnectionRequest = async () => {
    if (!selectedParent || !selectedMyChild) {
      alert('Please select your child');
      return;
    }


    if (selectedTargetChildren.length === 0) {
      alert('Please select at least one target child');
      return;
    }

    // Find the child objects to get their UUIDs
    const myChildObj = myChildren.find(child => child.uuid === selectedMyChild);
    
    try {
      let successCount = 0;
      let errorCount = 0;
      const errorDetails: Array<{childName: string, error: string}> = [];
      
      // Send separate connection request for each selected target child
      for (const targetChildUuid of selectedTargetChildren) {
        const targetChildObj = selectedParent.children.find(child => child.uuid === targetChildUuid);
        
        const requestData = {
          target_parent_uuid: selectedParent.user_uuid,
          child_uuid: myChildObj?.uuid,
          target_child_uuid: targetChildObj?.uuid,
          message: connectionMessage.trim() || undefined,
        };


        const response = await apiService.sendConnectionRequest(requestData);

        if (response.success) {
          successCount++;
        } else {
          errorCount++;
          console.error(`Failed to send request to ${targetChildObj?.name}:`, response.error);
          
          // Store specific error details for better user feedback
          errorDetails.push({
            childName: targetChildObj?.name || 'Unknown child',
            error: response.error || 'Unknown error'
          });
        }
      }

      if (successCount > 0) {
        setShowConnectModal(false);
        setSearchResults([]);
        setSearchEmail('');
        setSearchPhone('');
        // Reload both sent and received requests
        loadConnectionRequests();
        loadSentRequests();

        const message = successCount === 1
          ? 'Connection request sent successfully'
          : `${successCount} connection requests sent successfully${errorCount > 0 ? ` (${errorCount} failed)` : ''}`;

        // Show popup if user came from activity creation flow
        if (cameFromActivity) {
          setShowReturnToActivityPopup(true);
        } else {
          alert(message);
        }
      } else {
        // Show specific error messages for failed requests
        if (errorDetails.length > 0) {
          // Collect unique error messages
          const errorMap = new Map<string, string[]>();
          
          errorDetails.forEach(({childName, error}) => {
            let friendlyError = error;
            
            // Convert common backend errors to user-friendly messages
            if (error.includes('already exists') || error.includes('pending connection')) {
              friendlyError = 'A connection request is already pending';
            } else if (error.includes('Target parent not found')) {
              friendlyError = 'Target parent not found';
            } else if (error.includes('Child not found')) {
              friendlyError = 'Child not found';
            } else if (error.includes('Invalid credentials') || error.includes('Unauthorized')) {
              friendlyError = 'Authentication failed - please log in again';
            }
            
            if (!errorMap.has(friendlyError)) {
              errorMap.set(friendlyError, []);
            }
            errorMap.get(friendlyError)!.push(childName);
          });
          
          // Build user-friendly error message
          let errorMessage = 'Connection request issues:\n\n';
          errorMap.forEach((children, error) => {
            if (children.length === 1) {
              errorMessage += `• ${children[0]}: ${error}\n`;
            } else {
              errorMessage += `• ${children.join(', ')}: ${error}\n`;
            }
          });
          
          alert(errorMessage);
        } else {
          alert('Failed to send connection requests. Please try again.');
        }
      }
    } catch (error) {
      alert('Error sending connection requests');
      console.error('Send request error:', error);
    }
  };

  const handleCreateSkeletonAccount = async () => {
    // Check if using new first/last name fields or legacy combined field
    const hasFirstName = skeletonChildFirstName.trim();
    const hasLastName = skeletonChildLastName.trim();
    const hasLegacyName = skeletonChildName.trim();

    if (!selectedMyChild || (!hasFirstName && !hasLegacyName)) {
      alert('Please select your child and enter the target child\'s first name');
      return;
    }

    try {
      const myChildObj = myChildren.find(child => child.uuid === selectedMyChild);

      // Determine contact type (email or phone)
      const contactType = noResultsContact.includes('@') ? 'email' : 'phone';

      // Use first/last name if provided, otherwise fall back to legacy combined name
      let targetChildName;
      let targetChildFirstName = null;
      let targetChildLastName = null;

      if (hasFirstName) {
        targetChildName = `${skeletonChildFirstName.trim()} ${skeletonChildLastName.trim()}`.trim();
        targetChildFirstName = skeletonChildFirstName.trim();
        targetChildLastName = skeletonChildLastName.trim() || null;
      } else {
        targetChildName = skeletonChildName.trim();
        // Try to split legacy name into first/last
        const nameParts = skeletonChildName.trim().split(' ');
        targetChildFirstName = nameParts[0] || null;
        targetChildLastName = nameParts.slice(1).join(' ') || null;
      }

      const requestData = {
        contact_method: noResultsContact,
        contact_type: contactType,
        my_child_uuid: selectedMyChild,
        target_parent_name: skeletonParentName.trim() || null,
        target_child_name: targetChildName,
        target_child_first_name: targetChildFirstName,
        target_child_last_name: targetChildLastName,
        target_child_birth_year: skeletonChildBirthYear ? parseInt(skeletonChildBirthYear) : null,
        message: connectionMessage.trim() || `${myChildObj?.name} would like to connect with ${targetChildName}`
      };


      const response = await apiService.createSkeletonAccount(requestData);

      if (response.success) {
        setShowSkeletonModal(false);
        // Reset form
        setNoResultsContact('');
        setSkeletonParentName('');
        setSkeletonChildName('');
        setSkeletonChildFirstName('');
        setSkeletonChildLastName('');
        setSkeletonChildBirthYear('');
        setConnectionMessage('');
        setSelectedMyChild(null);
        setSearchEmail('');
        setSearchPhone('');
        setSearchResults([]);

        // Reload sent requests to show the new skeleton connection request

        // Add a small delay to ensure backend has processed the request
        setTimeout(async () => {


          // Load sent requests and get the fresh data directly from the API response
          const sentRequestsResponse = await apiService.getSentConnectionRequests();

          if (sentRequestsResponse.success && sentRequestsResponse.data) {
            const freshSentRequests = sentRequestsResponse.data;

            // Update the state with fresh data
            setSentRequests(freshSentRequests);


            // Log all requests to see what we got
            freshSentRequests.forEach((request, index) => {
            });

            // Check if our skeleton connection appears in the fresh results
            const skeletonConnections = freshSentRequests.filter(req => req.is_skeleton === true);

            // Look for connection matching our contact method in the fresh data
            const matchingConnection = freshSentRequests.find(req =>
              req.target_parent_email === noResultsContact ||
              req.target_parent_phone === noResultsContact ||
              req.contact_method === noResultsContact
            );

            if (matchingConnection) {
            } else {
              console.warn('⚠️ No matching connection found for contact:', noResultsContact);
              console.warn('📧 All current fresh sent requests:', freshSentRequests);
            }

            if (skeletonConnections.length === 0) {
              console.warn('⚠️ No skeleton connections found after creation. Possible backend issue.');
            } else {
            }
          } else {
            console.error('❌ Failed to reload sent requests after skeleton creation:', sentRequestsResponse.error);
          }
        }, 1000);

        // Show popup if user came from activity creation flow, otherwise alert
        if (cameFromActivity) {
          setShowReturnToActivityPopup(true);
        } else {
          alert(`Skeleton account created! When someone creates an account with ${noResultsContact}, they will receive your connection request.`);
        }
      } else {
        alert(`Error: ${response.error || 'Failed to create skeleton account'}`);
      }
    } catch (error) {
      alert('Failed to create skeleton account');
      console.error('Create skeleton account error:', error);
    }
  };

  return (
    <div className="connections-screen">
      <div className="connections-header">
        <h2>Connections</h2>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <h3>Connect with Parents</h3>

        {/* Search Method Toggle */}
        <div className="search-method-toggle" style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '15px'
        }}>
          <button
            className={`method-btn ${searchMethod === 'email' ? 'active' : ''}`}
            onClick={() => setSearchMethod('email')}
            style={{
              flex: 1,
              padding: '10px',
              border: 'none',
              borderRadius: '8px',
              background: searchMethod === 'email' ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#e9ecef',
              color: searchMethod === 'email' ? 'white' : '#6c757d',
              fontWeight: searchMethod === 'email' ? 'bold' : 'normal',
              cursor: 'pointer'
            }}
          >
            Email
          </button>
          <button
            className={`method-btn ${searchMethod === 'phone' ? 'active' : ''}`}
            onClick={() => setSearchMethod('phone')}
            style={{
              flex: 1,
              padding: '10px',
              border: 'none',
              borderRadius: '8px',
              background: searchMethod === 'phone' ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#e9ecef',
              color: searchMethod === 'phone' ? 'white' : '#6c757d',
              fontWeight: searchMethod === 'phone' ? 'bold' : 'normal',
              cursor: 'pointer'
            }}
          >
            Phone
          </button>
        </div>

        {/* Search Input */}
        <div className="search-form">
          {searchMethod === 'email' ? (
            <input
              type="email"
              placeholder="Enter email address"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="search-input"
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #e9ecef',
                fontSize: '16px'
              }}
            />
          ) : (
            <div style={{ flex: 1 }}>
              <PhoneInput
                value={searchPhone}
                onChange={setSearchPhone}
                placeholder="Enter phone number"
                className="search-phone-input"
              />
            </div>
          )}
          <button
            onClick={handleSearchParent}
            disabled={searching}
            className={`search-btn ${searching ? 'disabled' : ''}`}
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              background: searching ? '#ccc' : 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              fontWeight: 'bold',
              cursor: searching ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {searching ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="search-results">
          <h4>Search Results</h4>
          {searchResults.map((parent) => {
            const actualChildren = parent.children.filter((c: any) => !c.is_parent);
            const parentRecords = parent.children.filter((c: any) => c.is_parent);
            return (
              <div key={parent.user_uuid || parent.id} className="parent-card">
                <div className="parent-info">
                  <h5>{parent.username}</h5>
                  <div className="parent-contact">{parent.email}</div>
                  <div className="children-count">
                    {actualChildren.length} child{actualChildren.length !== 1 ? 'ren' : ''}
                    {parentRecords.length > 0 && `, ${parentRecords.length} parent${parentRecords.length !== 1 ? 's' : ''}`}
                  </div>
                </div>
                <button
                  onClick={() => handleConnectRequest(parent)}
                  className="connect-btn"
                >
                  Connect
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Connection Requests */}
      <div className="requests-section">
        <h3>Connection Requests ({connectionRequests.length})</h3>
        
        {loading ? (
          <div className="loading">Loading requests...</div>
        ) : connectionRequests.length === 0 ? (
          <div className="empty-state">
            <p>No connection requests</p>
          </div>
        ) : (
          <div className="requests-grouped">
            {Object.entries(
              connectionRequests.reduce((grouped: any, request) => {
                const targetChildName = request.target_child_name || 'Any Child';
                
                if (!grouped[targetChildName]) {
                  grouped[targetChildName] = [];
                }
                
                grouped[targetChildName].push(request);
                
                return grouped;
              }, {})
            ).map(([childName, requests]: any) => (
              <div key={childName} className="child-connections-group">
                <h4 className="child-group-header">{childName}</h4>
                <div className="child-connections-list">
                  {requests.map((request: any) => (
                    <div key={request.request_uuid} className="connection-item">
                      <span className="connected-to">
                        {request.class_name ? (
                          <>
                            <strong>{request.requester_name}</strong> has added{' '}
                            {request.target_child_name || 'your child'} to the class{' '}
                            <strong>{request.class_name}</strong>
                          </>
                        ) : (
                          <><strong>{request.child_name}</strong> wants to connect</>
                        )}
                      </span>
                      <div className="request-actions">
                        <button
                          onClick={() => handleRejectRequest(request.request_uuid)}
                          className="reject-btn"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => handleAcceptRequest(request.request_uuid)}
                          className="accept-btn"
                        >
                          Accept
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sent Connection Requests */}
      <div className="requests-section">
        <h3>Sent Requests ({sentRequests.length})</h3>
        
        {sentRequests.length === 0 ? (
          <div className="empty-state">
            <p>No sent requests</p>
          </div>
        ) : (
          <div className="requests-grouped">
            {Object.entries(
              sentRequests.reduce((grouped: any, request) => {
                const childName = request.child_name || 'Unknown Child';
                
                if (!grouped[childName]) {
                  grouped[childName] = [];
                }
                
                grouped[childName].push(request);
                
                return grouped;
              }, {})
            ).map(([childName, requests]: any) => (
              <div key={childName} className="child-connections-group">
                <h4 className="child-group-header">{childName}</h4>
                <div className="child-connections-list">
                  {requests.map((request: any) => (
                    <div key={request.request_uuid} className="connection-item">
                      <span className="connected-to">
                        {request.target_child_name ? (
                          <strong>{request.target_child_name}</strong>
                        ) : (
                          <span>Request sent to <strong>{request.target_parent_name || request.target_family_name}</strong> (Any Child)</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Active Connections Section */}
      <div className="connections-section">
        <h3>Active Connections ({activeConnections.length})</h3>
        
        {activeConnections.length === 0 ? (
          <div className="empty-state">
            <p>No active connections yet</p>
          </div>
        ) : (
          <div className="connections-grouped">
            {Object.entries(
              activeConnections.reduce((grouped: any, connection) => {
                // Determine which child belongs to the current user by checking against myChildren
                let myChild, theirChild;

                // Check if child1 is one of my children
                const child1IsMine = myChildren.some(child => child.uuid === connection.child1_uuid);

                if (child1IsMine) {
                  myChild = { name: connection.child1_name, uuid: connection.child1_uuid };
                  theirChild = { name: connection.child2_name, uuid: connection.child2_uuid };
                } else {
                  myChild = { name: connection.child2_name, uuid: connection.child2_uuid };
                  theirChild = { name: connection.child1_name, uuid: connection.child1_uuid };
                }

                // Group by our child (parent's child)
                if (!grouped[myChild.uuid]) {
                  grouped[myChild.uuid] = {
                    childName: myChild.name,
                    connections: []
                  };
                }

                grouped[myChild.uuid].connections.push({
                  ...connection,
                  connectedToName: theirChild.name
                });

                return grouped;
              }, {})
            ).map(([childId, childGroup]: any) => (
              <div key={childId} className="child-connections-group">
                <h4 className="child-group-header">{childGroup.childName}</h4>
                <div className="child-connections-list">
                  {childGroup.connections.map((connection: any) => (
                    <div key={connection.connection_uuid} className="connection-item">
                      <span className="connected-to"><strong>{connection.connectedToName}</strong></span>
                      <button
                        onClick={() => {
                          handleRemoveConnection(
                            connection.connection_uuid,
                            `${childGroup.childName} ↔ ${connection.connectedToName}`
                          );
                        }}
                        className="remove-connection-btn"
                        title="Remove this connection"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Connection Request Modal */}
      {showConnectModal && selectedParent && (
        <div className="modal-overlay" onClick={() => setShowConnectModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Send Connection Request</h3>
            
            <div className="form-section">
              <label>Who is connecting?</label>

              {/* Children Section */}
              {myChildren.filter(c => !c.is_parent).length > 0 && (
                <>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '6px', marginTop: '8px' }}>Children:</div>
                  <div className="child-selector">
                    {myChildren.filter(c => !c.is_parent).map((child) => (
                      <button
                        key={child.uuid}
                        className={`child-option ${selectedMyChild === child.uuid ? 'selected' : ''}`}
                        onClick={() => setSelectedMyChild(child.uuid)}
                      >
                        {child.display_name || child.name}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Parents Section */}
              {myChildren.filter(c => c.is_parent).length > 0 && (
                <>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '6px', marginTop: '12px' }}>Parents:</div>
                  <div className="child-selector">
                    {myChildren.filter(c => c.is_parent).map((parent) => (
                      <button
                        key={parent.uuid}
                        className={`child-option ${selectedMyChild === parent.uuid ? 'selected' : ''}`}
                        onClick={() => setSelectedMyChild(parent.uuid)}
                        style={{
                          background: selectedMyChild === parent.uuid
                            ? 'linear-gradient(135deg, #9575cd, #7e57c2)'
                            : undefined
                        }}
                      >
                        👔 {parent.display_name || parent.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="form-section">
              <label>Connect with:</label>
              <div className="selection-help" style={{
                fontSize: '12px',
                color: '#6c757d',
                marginBottom: '8px',
                background: '#ffffcc',
                padding: '4px',
                borderRadius: '4px'
              }}>
                Click to select/deselect. Multiple selections allowed.
              </div>

              {/* Children Section */}
              {selectedParent.children.filter((c: any) => !c.is_parent).length > 0 && (
                <>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '6px', marginTop: '8px' }}>Children:</div>
                  <div className="child-selector">
                    {selectedParent.children.filter((c: any) => !c.is_parent).length > 1 && (
                      <button
                        className={`child-option ${selectedTargetChildren.length === selectedParent.children.filter((c: any) => !c.is_parent).length ? 'selected' : ''}`}
                        onClick={() => {
                          const actualChildren = selectedParent.children.filter((c: any) => !c.is_parent);
                          if (selectedTargetChildren.length === actualChildren.length) {
                            setSelectedTargetChildren([]);
                          } else {
                            setSelectedTargetChildren(actualChildren.map((child: any) => child.uuid));
                          }
                        }}
                        style={{
                          background: selectedTargetChildren.length === selectedParent.children.filter((c: any) => !c.is_parent).length
                            ? 'linear-gradient(135deg, #667eea, #764ba2)'
                            : '#e9ecef',
                          color: selectedTargetChildren.length === selectedParent.children.filter((c: any) => !c.is_parent).length
                            ? 'white'
                            : '#6c757d'
                        }}
                      >
                        All Children ({selectedParent.children.filter((c: any) => !c.is_parent).length})
                      </button>
                    )}
                    {selectedParent.children.filter((c: any) => !c.is_parent).map((child: any) => (
                      <button
                        key={child.uuid}
                        className={`child-option ${selectedTargetChildren.includes(child.uuid) ? 'selected' : ''}`}
                        onClick={() => {
                          if (selectedTargetChildren.includes(child.uuid)) {
                            setSelectedTargetChildren(selectedTargetChildren.filter(uuid => uuid !== child.uuid));
                          } else {
                            setSelectedTargetChildren([...selectedTargetChildren, child.uuid]);
                          }
                        }}
                        style={{
                          background: selectedTargetChildren.includes(child.uuid)
                            ? 'linear-gradient(135deg, #667eea, #764ba2)'
                            : '#e9ecef',
                          color: selectedTargetChildren.includes(child.uuid)
                            ? 'white'
                            : '#6c757d'
                        }}
                      >
                        {child.display_name || child.name}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Parents Section */}
              {selectedParent.children.filter((c: any) => c.is_parent).length > 0 && (
                <>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '6px', marginTop: '12px' }}>Parents:</div>
                  <div className="child-selector">
                    {selectedParent.children.filter((c: any) => c.is_parent).map((parent: any) => (
                      <button
                        key={parent.uuid}
                        className={`child-option ${selectedTargetChildren.includes(parent.uuid) ? 'selected' : ''}`}
                        onClick={() => {
                          if (selectedTargetChildren.includes(parent.uuid)) {
                            setSelectedTargetChildren(selectedTargetChildren.filter(uuid => uuid !== parent.uuid));
                          } else {
                            setSelectedTargetChildren([...selectedTargetChildren, parent.uuid]);
                          }
                        }}
                        style={{
                          background: selectedTargetChildren.includes(parent.uuid)
                            ? 'linear-gradient(135deg, #9575cd, #7e57c2)'
                            : '#e9ecef',
                          color: selectedTargetChildren.includes(parent.uuid)
                            ? 'white'
                            : '#6c757d'
                        }}
                      >
                        👔 {parent.display_name || parent.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="form-section">
              <label>Message (Optional):</label>
              <textarea
                placeholder="Hi! Would love to connect our kids..."
                value={connectionMessage}
                onChange={(e) => setConnectionMessage(e.target.value)}
                className="message-input"
              />
            </div>

            <div className="modal-actions">
              <button
                onClick={() => setShowConnectModal(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleSendConnectionRequest}
                className="send-btn"
              >
                Send Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Return to Activity Popup */}
      {showReturnToActivityPopup && (
        <div className="modal-overlay" style={{
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <h3>Connection Request Sent! 🎉</h3>
            <p>Your connection request has been sent successfully.</p>
            <p>Would you like to continue adding more connections or return to finish creating your activity?</p>
            
            <div className="modal-actions">
              <button
                onClick={() => setShowReturnToActivityPopup(false)}
                className="cancel-btn"
                style={{
                  background: 'linear-gradient(135deg, #48bb78, #68d391)',
                  color: 'white',
                  border: 'none'
                }}
              >
                Continue Adding Connections
              </button>
              <button
                onClick={() => {
                  setShowReturnToActivityPopup(false);
                  if (returnToActivityUrl) {
                    // Force the Dashboard to update its active tab first
                    if (onForceTabUpdate) {
                      onForceTabUpdate('children');
                    }
                    // Add connectionUpdate parameter to tell activity screen to reload connections
                    const separator = returnToActivityUrl.includes('?') ? '&' : '?';
                    const urlWithUpdate = `${returnToActivityUrl}${separator}connectionUpdate=true`;
                    // Then navigate
                    navigate(urlWithUpdate, { replace: false });
                  } else if (onReturnToActivity) {
                    onReturnToActivity();
                  }
                }}
                className="send-btn"
                style={{
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: 'white',
                  border: 'none'
                }}
              >
                Return to Activity
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Skeleton Account Creation Modal */}
      {showSkeletonModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
          }}>
            <h3>No Account Found</h3>
            <p>No parent found with <strong>{noResultsContact}</strong>.</p>
            <p>Would you like to create a connection request for when they sign up?</p>
            
            <div className="form-section">
              <label>Who is connecting?</label>
              <select
                value={selectedMyChild || ''}
                onChange={(e) => setSelectedMyChild(e.target.value)}
                className="child-select"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid #e9ecef',
                  fontSize: '16px'
                }}
              >
                <option value="">Choose...</option>
                {myChildren.filter(c => !c.is_parent).length > 0 && (
                  <optgroup label="Children">
                    {myChildren.filter(c => !c.is_parent).map((child) => (
                      <option key={child.uuid} value={child.uuid}>
                        {child.display_name || child.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                {myChildren.filter(c => c.is_parent).length > 0 && (
                  <optgroup label="Parents">
                    {myChildren.filter(c => c.is_parent).map((parent) => (
                      <option key={parent.uuid} value={parent.uuid}>
                        {parent.display_name || parent.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>

            <div className="form-section">
              <label>Parent's Name (Optional):</label>
              <input
                type="text"
                placeholder="e.g., Sarah Smith"
                value={skeletonParentName}
                onChange={(e) => setSkeletonParentName(e.target.value)}
                className="name-input"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid #e9ecef',
                  fontSize: '16px'
                }}
              />
              <small style={{ color: '#666', fontSize: '13px', marginTop: '4px', display: 'block' }}>
                If you know the parent's name, it will help them identify your request
              </small>
            </div>

            <div className="form-section">
              <label>Target Child's First Name: *</label>
              <input
                type="text"
                placeholder="Enter the child's first name"
                value={skeletonChildFirstName}
                onChange={(e) => setSkeletonChildFirstName(e.target.value)}
                className="name-input"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid #e9ecef',
                  fontSize: '16px'
                }}
              />
            </div>

            <div className="form-section">
              <label>Target Child's Last Name:</label>
              <input
                type="text"
                placeholder="Enter the child's last name (optional)"
                value={skeletonChildLastName}
                onChange={(e) => setSkeletonChildLastName(e.target.value)}
                className="name-input"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid #e9ecef',
                  fontSize: '16px'
                }}
              />
            </div>

            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button
                onClick={() => {
                  setShowSkeletonModal(false);
                  setNoResultsContact('');
                  setSkeletonParentName('');
                  setSkeletonChildName('');
                  setSkeletonChildFirstName('');
                  setSkeletonChildLastName('');
                  setSkeletonChildBirthYear('');
                  setConnectionMessage('');
                  setSelectedMyChild(null);
                }}
                className="cancel-btn"
                style={{
                  padding: '12px 20px',
                  marginRight: '10px',
                  borderRadius: '8px',
                  border: '2px solid #dc3545',
                  backgroundColor: 'white',
                  color: '#dc3545',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSkeletonAccount}
                className="send-btn"
                style={{
                  padding: '12px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: 'white',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
                disabled={!selectedMyChild || !skeletonChildFirstName.trim()}
              >
                Create Connection Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Navigation Bar */}
      {cameFromOnboarding && onContinueOnboarding && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '12px 20px',
          paddingBottom: 'max(12px, env(safe-area-inset-bottom, 12px))',
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{ fontSize: '14px', color: '#666' }}>
            Setting up your account...
          </div>
          <button
            onClick={onContinueOnboarding}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Continue →
          </button>
        </div>
      )}
    </div>
  );
};

export default ConnectionsScreen;